import React, { useState } from 'react';
import { Settings, Shield, Eye, Bell, MapPin, Cookie as CookieIcon } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from '@/App';

const PrivacySettingsPage = () => {
  const [settings, setSettings] = useState({
    // Cookies
    essentialCookies: true, // Always enabled
    performanceCookies: true,
    functionalCookies: true,
    
    // Privacy
    showProfile: true,
    showActivity: false,
    shareDataPartners: false,
    
    // Notifications
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    
    // Location
    trackLocation: true,
    shareLocationHistory: false
  });

  const [saving, setSaving] = useState(false);

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      toast.success('Paramètres de confidentialité enregistrés');
      setSaving(false);
    }, 1000);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-1">
        <section className="bg-gradient-to-br from-red-50 to-cyan-50 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Settings className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h1 className="text-4xl font-bold gradient-text mb-4">
                Paramètres de Confidentialité
              </h1>
              <p className="text-gray-600">
                Contrôlez vos données et votre vie privée sur Coli Noki
              </p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-6">
              
              {/* Cookies */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CookieIcon className="w-5 h-5 text-amber-600" />
                    Gestion des Cookies
                  </CardTitle>
                  <CardDescription>
                    Contrôlez les cookies utilisés sur notre plateforme
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Cookies essentiels</p>
                      <p className="text-sm text-gray-600">Nécessaires au fonctionnement du site</p>
                    </div>
                    <Switch checked={true} disabled />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Cookies de performance</p>
                      <p className="text-sm text-gray-600">Nous aident à améliorer le site</p>
                    </div>
                    <Switch 
                      checked={settings.performanceCookies} 
                      onCheckedChange={() => handleToggle('performanceCookies')}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Cookies fonctionnels</p>
                      <p className="text-sm text-gray-600">Mémorisent vos préférences</p>
                    </div>
                    <Switch 
                      checked={settings.functionalCookies} 
                      onCheckedChange={() => handleToggle('functionalCookies')}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Privacy */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Confidentialité du Profil
                  </CardTitle>
                  <CardDescription>
                    Gérez la visibilité de vos informations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Profil public</p>
                      <p className="text-sm text-gray-600">Rendre votre profil visible aux autres utilisateurs</p>
                    </div>
                    <Switch 
                      checked={settings.showProfile} 
                      onCheckedChange={() => handleToggle('showProfile')}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Afficher l'activité</p>
                      <p className="text-sm text-gray-600">Partager votre activité récente</p>
                    </div>
                    <Switch 
                      checked={settings.showActivity} 
                      onCheckedChange={() => handleToggle('showActivity')}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Partage avec partenaires</p>
                      <p className="text-sm text-gray-600">Partager vos données avec nos partenaires de confiance</p>
                    </div>
                    <Switch 
                      checked={settings.shareDataPartners} 
                      onCheckedChange={() => handleToggle('shareDataPartners')}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-blue-600" />
                    Notifications
                  </CardTitle>
                  <CardDescription>
                    Choisissez comment vous souhaitez être notifié
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Notifications par email</p>
                      <p className="text-sm text-gray-600">Recevoir des emails sur vos livraisons</p>
                    </div>
                    <Switch 
                      checked={settings.emailNotifications} 
                      onCheckedChange={() => handleToggle('emailNotifications')}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Notifications push</p>
                      <p className="text-sm text-gray-600">Notifications en temps réel sur l'application</p>
                    </div>
                    <Switch 
                      checked={settings.pushNotifications} 
                      onCheckedChange={() => handleToggle('pushNotifications')}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Notifications SMS</p>
                      <p className="text-sm text-gray-600">Recevoir des SMS pour les événements importants</p>
                    </div>
                    <Switch 
                      checked={settings.smsNotifications} 
                      onCheckedChange={() => handleToggle('smsNotifications')}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Location */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-600" />
                    Géolocalisation
                  </CardTitle>
                  <CardDescription>
                    Gérez l'utilisation de votre position
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Suivi de position</p>
                      <p className="text-sm text-gray-600">Permettre le suivi GPS pendant les livraisons</p>
                    </div>
                    <Switch 
                      checked={settings.trackLocation} 
                      onCheckedChange={() => handleToggle('trackLocation')}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Historique de position</p>
                      <p className="text-sm text-gray-600">Sauvegarder l'historique de vos déplacements</p>
                    </div>
                    <Switch 
                      checked={settings.shareLocationHistory} 
                      onCheckedChange={() => handleToggle('shareLocationHistory')}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default PrivacySettingsPage;